import { GoogleMap, Libraries } from "@react-google-maps/api";
import { ReactNode, useCallback, useEffect, useState } from "react";

const containerStyle = {
  width: "100%",
  minHeight: "100%",
  height: "100%" // mejor asegurar height explícita
};

export const libraries: Libraries = ["geocoding", "places", "geometry", "routes"];

type Props = {
  center?: { lat: number; lng: number };
  bounds?: { lat: number; lng: number }[];
  children?: ReactNode;
};

function GoogleMapsCMP(props: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [error, setError] = useState<string>("");

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  const onError = useCallback((e: any) => {
    console.error("Google Maps load error:", e);
    setError("Error al cargar Google Maps API");
  }, []);

  useEffect(() => {
    if (!map) return;
    if (!props.bounds || props.bounds.length === 0) {
      // si no hay bounds usamos center (si existe)
      if (props.center) {
        map.setCenter(props.center);
        map.setZoom(14);
      }
      return;
    }

    // Validar coordenadas
    const pts = props.bounds.filter(p =>
      isFinite(p.lat) && isFinite(p.lng)
    );
    if (pts.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    pts.forEach(p => bounds.extend(p));

    // Hacemos fitBounds tras un pequeño delay para asegurar layout renderizado
    // (muy útil si el mapa está dentro de un contenedor que se renderiza/dimens.)
    const timeout = window.setTimeout(() => {
      try {
        // Caso: todos los puntos son el mismo -> fitBounds no cambia zoom útilmente
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const samePoint = ne && sw && ne.equals && ne.equals(sw);

        if (pts.length === 1 || samePoint) {
          // enfocamos el punto con zoom razonable
          map.setCenter(pts[0]);
          map.setZoom(15); // ajusta zoom por preferencia
        } else {
          // fitBounds con padding (segundo arg)
          // nota: si tu versión no acepta padding como número, usa map.fitBounds(bounds) y luego map.setCenter(...)
          map.fitBounds(bounds, 80); // 80px padding
          // limitar zoom si queda demasiado cercano
          google.maps.event.addListenerOnce(map, "idle", () => {
            const z = map.getZoom();
            if (z && z > 16) map.setZoom(16);
          });
        }

        // debug opcional:
        console.log("fitBounds applied. bounds NE/SW:", bounds.getNorthEast(), bounds.getSouthWest(), "map zoom:", map.getZoom());
      } catch (err) {
        console.error("Error applying fitBounds:", err);
      }
    }, 200); // 200ms suele ser suficiente

    return () => window.clearTimeout(timeout);
  }, [map, props.bounds, props.center]);

  // No pasar center/zoom cuando se usan bounds, ya que pueden sobreescribir fitBounds
  const googleMapProps: any = {};
  if (!props.bounds || props.bounds.length === 0) {
    if (props.center) {
      googleMapProps.center = props.center;
      googleMapProps.zoom = 14;
    }
  }

  return (
    <>
      {error && <div style={{ color: "red" }}>{error}</div>}
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onError={onError}
        {...googleMapProps}
      >
        {props.children}
      </GoogleMap>
    </>
  );
}

export default GoogleMapsCMP;